import { styled } from "@linaria/react";
import React from "react";
import { color, fonts, stableColor } from "@ui";
import Actions from "./Actions";
import Link from "@ui/Link";
import FavoriteButtonIcon from "@ui/FavoriteButtonIcon";
import t from "../../i18n";
import { HIDE_ON_EXPORT } from "@ui/lib/downloadPage";
import { EmptyCard } from "@ui/Card";
import type { ProjectModalType } from "./ProjectModalType";
import type { Project } from "../../api/useProjects";
import useToggleFavoriteProject from "./useToggleFavoriteProject";
import { useUser } from "../../api/useUser";
import { getBadgeStableColors } from "@ui/Badge/utils";
import { FlaskFill } from "@ui/IconV2/react";
import TooltipV2 from "@ui/TooltipV2";
import OverlayWrapper from "@ui/TooltipV2/OverlayWrapper";
import formatNumber from "@ui/i18n/formatNumber";
import { LastAnalysisProject } from "./LastAnalysisProject";
import Badge from "@ui/Badge";
import { badgeTextOverflow } from "@ui/Badge/_styles/BadgeTextOverflow";
import { useTranslations } from "../../api/useTranslations";
import { toPath } from "../../router/helpers/path";

const BadgesContainer = styled.div`
  display: flex;
  gap: 8px;
`;

const StyledActions = styled(Actions)`
  display: none;
  margin-left: 8px;
`;

const ProjectCard = styled(EmptyCard)<{ color: string }>`
  display: flex;
  cursor: pointer;
  border-left: 4px solid ${p => p.color};
  height: 192px;

  &:hover ${StyledActions} {
    display: flex;
  }
`;

const TopPart = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  border-bottom: 1px solid ${color.titanium200};
  padding: 8px 16px;
`;

const Header = styled.div`
  height: 32px;
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const BottomPart = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex-grow: 1;
  padding: 8px 15px;

  // Details
  & > div:first-child {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    gap: 4px;

    // Infos
    & > p {
      display: flex;
      justify-content: space-between;

      span:first-child {
        font: ${fonts.body2};
      }
      span:last-child {
        font: ${fonts.body2SemiBold};
      }
    }
  }
`;

const Title = styled.div`
  margin-left: -6px;
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
`;

const Name = styled.div`
  color: ${color.carbon900};
  font: ${fonts.h4};
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
`;

const StyledLab = styled(FlaskFill)`
  display: flex;
  align-items: baseline;
  color: ${color.carbon800};
  width: 14px;
  height: 14px;
`;

interface P {
  project: Project;
  openModal: (type: ProjectModalType, project: Project) => void;
}

const FavouriteCard = ({ project, openModal }: P) => {
  const translations = useTranslations();
  const toggleFavouriteProject = useToggleFavoriteProject();
  const siteColor =
    project.siteId === null ? color.titanium200 : stableColor(String(project.siteId));
  const user = useUser();

  const openEditModal = React.useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      openModal("update", project);
    },
    [openModal, project],
  );

  const openDeleteModal = React.useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      openModal("delete", project);
    },
    [openModal, project],
  );

  const openDuplicateModal = React.useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      openModal("duplicate", project);
    },
    [openModal, project],
  );

  const aggregate = project.analysesAggregate;
  const analysis = aggregate === null ? null : aggregate.latestActiveAnalysis;
  const quantity = analysis === null ? null : analysis.quantity;
  const confirmedFailureCount = analysis?.confirmedFailureCount ?? null;

  return (
    <Link to={toPath("/projects/:projectId", { projectId: project.id })}>
      <ProjectCard data-cy={project.name.replace(" ", "-")} color={siteColor}>
        <TopPart>
          <Header>
            <Title>
              <FavoriteButtonIcon
                isFavorite
                onClick={e => {
                  e.preventDefault();
                  void toggleFavouriteProject(project.id, true);
                }}
              />
              <Name>{project.name}</Name>
              {project.draft && (
                <TooltipV2 overlay={<OverlayWrapper text={t("commons.tooltip.research")} />}>
                  <span>
                    <StyledLab />
                  </span>
                </TooltipV2>
              )}
            </Title>
            <StyledActions
              user={user}
              project={project}
              openEditModal={openEditModal}
              openDeleteModal={openDeleteModal}
              openDuplicateModal={openDuplicateModal}
              className={HIDE_ON_EXPORT}
            />
          </Header>
          {(project.siteId || project.typeId) && (
            <BadgesContainer>
              {project.siteId && (
                <Badge
                  className={badgeTextOverflow}
                  style={getBadgeStableColors(project.siteId)}
                  title={project.siteId}
                >
                  {project.siteId}
                </Badge>
              )}
              {project.typeId && (
                <Badge className={badgeTextOverflow} title={project.typeId}>
                  {project.typeId}
                </Badge>
              )}
            </BadgesContainer>
          )}
        </TopPart>
        <BottomPart>
          <div>
            {quantity === null ? null : (
              <>
                {quantity.name && quantity.measured !== null ? (
                  <p>
                    <span>{t("projects.favourite_card.title.power_produced")}</span>
                    <span>
                      {formatNumber(quantity.measured, {
                        precision: 1,
                        unit: quantity.unit ?? undefined,
                      })}
                    </span>
                  </p>
                ) : null}
                {quantity.internalLoss === null ? null : (
                  <p>
                    <span>{t("projects.favourite_card.title.internal_losses", translations)}</span>
                    <span>
                      {formatNumber(quantity.internalLoss, {
                        precision: 1,
                        unit: quantity.unit ?? undefined,
                      })}
                    </span>
                  </p>
                )}
              </>
            )}
            {confirmedFailureCount === null ? null : (
              <p>
                <span>{t("projects.favourite_card.title.confirmed_faults")}</span>
                <span>
                  {formatNumber(confirmedFailureCount, {
                    precision: 1,
                  })}
                </span>
              </p>
            )}
          </div>
          <LastAnalysisProject
            publishedAnalysesLastDate={analysis?.date ?? null}
            timezone={project.timezone}
          />
        </BottomPart>
      </ProjectCard>
    </Link>
  );
};

export default React.memo(FavouriteCard);
